import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, AlertCircle, ChevronRight, Info } from 'lucide-react';
import { getLymphocyteTrend } from '../utils/api';

const LymphocyteTrendCard = () => {
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrend = async () => {
      try {
        const data = await getLymphocyteTrend(24); // Default 24 months
        setTrendData(data);
      } catch (err) {
        console.error('Failed to fetch lymphocyte trend:', err);
        setError('Unable to load trend data');
      } finally {
        setLoading(false);
      }
    };
    fetchTrend();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
        <div className="h-10 bg-slate-200 rounded w-1/2 mb-2"></div>
        <div className="h-4 bg-slate-200 rounded w-2/3"></div>
      </div>
    );
  }

  if (error) {
    return null; // Silently fail - don't show card if there's an error
  }

  // Insufficient data state
  if (!trendData?.has_sufficient_data) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="lymphocyte-trend-card-empty">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-slate-100 rounded-xl">
            <TrendingUp className="h-6 w-6 text-slate-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Lymphocyte Trend Rate</h3>
            <p className="text-sm text-slate-600 mb-3">
              Add at least 3 lymphocyte results across 6+ months to see your trend rate.
            </p>
            <p className="text-xs text-slate-500">
              {trendData?.readings_used || 0} reading{trendData?.readings_used !== 1 ? 's' : ''} recorded
              {trendData?.months_spanned > 0 && ` over ${trendData.months_spanned} months`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Stable or decreasing trend
  if (trendData.trend_status === 'stable_or_decreasing') {
    return (
      <Link 
        to="/lymphocyte-trend"
        className="block bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:border-slate-300 transition-all"
        data-testid="lymphocyte-trend-card-stable"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-50 rounded-xl">
              {trendData.slope < -0.0001 ? (
                <TrendingDown className="h-6 w-6 text-green-600" />
              ) : (
                <Minus className="h-6 w-6 text-green-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Lymphocyte Trend Rate</h3>
              <p className="text-xl font-bold text-green-600 mb-2">
                {trendData.slope < -0.0001 ? 'Decreasing' : 'Stable'}
              </p>
              <p className="text-sm text-slate-600 mb-3">
                Your lymphocyte count has been stable or decreasing over this period.
              </p>
              <p className="text-xs text-slate-500">
                Based on {trendData.readings_used} readings over {Math.round(trendData.months_spanned)} months
              </p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </div>
        
        {/* Disclaimer */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-500 flex items-start gap-2">
            <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>This is a mathematical trend of your own entered results, not a medical assessment or prediction.</span>
          </p>
        </div>
      </Link>
    );
  }

  // Doubling trend - show the doubling time
  return (
    <Link 
      to="/lymphocyte-trend"
      className="block bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:border-slate-300 transition-all"
      data-testid="lymphocyte-trend-card"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 rounded-xl">
            <TrendingUp className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Lymphocyte Trend Rate</h3>
            <p className="text-2xl font-bold text-slate-900 mb-1">
              Doubling time {trendData.doubling_time_friendly}
            </p>
            <p className="text-sm text-slate-600 mb-3">
              How long it would take your lymphocyte count to double if the current trend continued.
            </p>
            <p className="text-xs text-slate-500">
              Based on {trendData.readings_used} readings over {Math.round(trendData.months_spanned)} months
            </p>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-400" />
      </div>
      
      {/* Disclaimer */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-xs text-slate-500 flex items-start gap-2">
          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>This is a mathematical trend of your own entered results, not a medical assessment or prediction. Doubling time is one of several factors doctors consider. Always discuss your results with your haematologist.</span>
        </p>
      </div>
    </Link>
  );
};

export default LymphocyteTrendCard;
